import styled from '@emotion/styled';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import { Section } from '@shared/components/core/layouts/CommonLayoutStyles';
import { FieldSetLabel } from '@shared/components/core/text/FormsTypography';
import { MacroDisplayEvent } from '@shared/models/deviceProfiles/MacroEvent';
import { MacroDisplayModel } from '@shared/models/deviceProfiles/MacroModel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// TODO MOVE TO COMPONENTS

export interface IStepMouseMoveProps {
    macro: MacroDisplayModel;
    event: MacroDisplayEvent;
    eventIndex: number;
    isPreview?: boolean;
    onChange: (macro: MacroDisplayModel) => void;
}

export default function StepMouseMove({ macro, event, eventIndex, isPreview = false, onChange }: IStepMouseMoveProps) {
    const { t } = useTranslation();
    const initMouseMoveX = event.mouseMoveX ?? 0;
    const initDirectionX = initMouseMoveX <= 0 ? 'left' : 'right';
    const initPixelsX = Math.abs(initMouseMoveX);
    const initMouseMoveY = event.mouseMoveY ?? 0;
    const initDirectionY = initMouseMoveY >= 0 ? 'down' : 'up';
    const initPixelsY = Math.abs(initMouseMoveY);

    const [moveX, setMoveX] = useState(initMouseMoveX ? initMouseMoveX : 0);
    const [directionX, setDirectionX] = useState(initDirectionX ? initDirectionX : 'right');
    const [pixelsX, setPixelsX] = useState(initPixelsX ? initPixelsX : 0);

    const [moveY, setMoveY] = useState(initMouseMoveY ? initMouseMoveY : 0);
    const [directionY, setDirectionY] = useState(initDirectionY ? initDirectionY : 'up');
    const [pixelsY, setPixelsY] = useState(initPixelsY ? initPixelsY : 0);

    const handleEventMouseXLeftRight = (value: string) => {
        const newMoveX = pixelsX * (value === 'left' ? -1 : 1);
        macro.events[eventIndex].mouseMoveX = newMoveX;
        onChange({ ...macro });
        setMoveX(newMoveX);
        setDirectionX(value);
    };

    const handleEventMouseXChange = (value: string) => {
        setPixelsX(Number(value));
        const newMoveX = Number(value) * (directionX === 'left' ? -1 : 1);
        macro.events[eventIndex].mouseMoveX = newMoveX;
        onChange({ ...macro });
        setMoveX(newMoveX);
    };

    const handleEventMouseYUpDown = (value: string) => {
        const newMoveY = moveY * -1;
        macro.events[eventIndex].mouseMoveY = newMoveY;
        onChange({ ...macro });
        setMoveY(newMoveY);
        setDirectionY(value);
    };

    const handleEventMouseYChange = (value: string) => {
        setPixelsY(Number(value));
        const newMoveY = Number(value) * (directionY === 'down' ? 1 : -1);
        macro.events[eventIndex].mouseMoveY = newMoveY;
        onChange({ ...macro });
        setMoveY(newMoveY);
    };

    return (
        <Section data-id="mouse-move-step">
            <fieldset>
                <FieldSetLabel>{t('common:event.mouseMove.moveX')}</FieldSetLabel>
                <Set>
                    <StyledRadioGroup
                        name={`${eventIndex}-horizontal-mouse`}
                        defaultValue={directionX}
                        onChange={(e) => handleEventMouseXLeftRight(e.target.value)}
                        value={directionX}>
                        <FormControlLabel
                            value="right"
                            control={<Radio disabled={isPreview} />}
                            label={t('common:event.directions.right')}
                        />
                        <FormControlLabel
                            value="left"
                            control={<Radio disabled={isPreview} />}
                            label={t('common:event.directions.left')}
                        />
                    </StyledRadioGroup>
                    <StyledTextField
                        disabled={isPreview}
                        type="number"
                        fullWidth
                        slotProps={{
                            htmlInput: { min: 0 },
                        }}
                        value={pixelsX}
                        label={t('common:event.mouseMove.numberMoveX')}
                        placeholder={t('common:event.mouseMove.numberMoveX')}
                        onChange={(e) => handleEventMouseXChange(e.target.value)}
                    />
                </Set>
            </fieldset>
            <fieldset>
                <FieldSetLabel>{t('common:event.mouseMove.moveY')}</FieldSetLabel>
                <Set>
                    <StyledRadioGroup
                        name={`${eventIndex}-vertical-mouse`}
                        defaultValue={directionY}
                        onChange={(e) => handleEventMouseYUpDown(e.target.value)}
                        value={directionY}>
                        <FormControlLabel
                            value="up"
                            control={<Radio disabled={isPreview} />}
                            label={t('common:event.directions.up')}
                        />
                        <FormControlLabel
                            value="down"
                            control={<Radio disabled={isPreview} />}
                            label={t('common:event.directions.down')}
                        />
                    </StyledRadioGroup>
                    <StyledTextField
                        disabled={isPreview}
                        type="number"
                        label={t('common:event.mouseMove.numberMoveY')}
                        fullWidth
                        slotProps={{
                            htmlInput: { min: 0 },
                        }}
                        value={pixelsY}
                        placeholder={t('common:event.mouseMove.numberMoveY')}
                        onChange={(e) => handleEventMouseYChange(e.target.value)}
                    />
                </Set>
            </fieldset>
        </Section>
    );
}

const StyledRadioGroup = styled(RadioGroup)`
    margin-bottom: 1rem;
`;

const Set = styled.div`
    padding: 0 0.5rem;
`;
